/**
 * ChunkHighlight — Absolute-positioned highlight overlay for an evidence chunk.
 *
 * Renders a semi-transparent yellow rectangle using the chunk's bounding box
 * coordinates (normalized 0-1 as percentages). Positioned inside a
 * relative-position page container.
 */
import React from 'react';
import type { ChunkBbox } from '../../types/viewer';

interface ChunkHighlightProps {
  bbox: ChunkBbox;
  pageWidth?: number | null;
  pageHeight?: number | null;
  /** Whether this highlight is the "active" one (focused via URL param) */
  isActive: boolean;
  /** When the matched WORD is boxed in the text layer, render this chunk
   *  rectangle as a faint outline so it gives context without competing. */
  subtle?: boolean;
  /** Optional click handler for selecting this chunk */
  onClick?: () => void;
  /** Chunk ID for accessibility. NULL for a page-keyed highlight: a
   *  progressive-read citation knows its page and its sentence exactly but owns
   *  no chunk, and the box is computed from the sentence against the page's word
   *  boxes — the chunk never contributed geometry, only an address. Such a
   *  highlight is marked `data-page-anchor` instead of `data-chunk-id`. */
  chunkId: number | null;
  /** When true, the highlight is a VISUAL-ONLY band that lets mouse events fall
   *  through to the layer beneath (``pointer-events: none``). Used by the
   *  bilingual jp_pdf / zh_pdf viewers, where the hover-translate region layer
   *  sits under the citation highlight — an interactive highlight would swallow
   *  the hover and block translating the cited paragraph. Non-bilingual viewers
   *  keep the clickable highlight (default false). */
  pointerThrough?: boolean;
  /** Draw a full-width horizontal band and ignore the bbox's x extent.
   *
   *  This was unconditional, and it is why no highlight ever looked like a
   *  highlighter: `left:0% width:100%` threw away the matched text's horizontal
   *  position, so a six-word citation painted the whole line and a one-line
   *  citation painted the whole column.
   *
   *  It stays available for the COARSE chunk-bbox fallback, where the rectangle
   *  describes a whole paragraph and a band genuinely reads better than a
   *  ragged box. The sentence path never wants it: those rects are already
   *  trimmed to the cited characters by `computeAnchorRects`. */
  fullWidth?: boolean;
  /** Which cited sentence this rect belongs to. Rects sharing an index are one
   *  piece of evidence wrapped across lines and get one colour; a different
   *  index is a DIFFERENT claim and gets a different colour, so a reader can
   *  see which words carry which part of the answer instead of one wash. */
  colorIndex?: number;
}

/** Distinguishable at 40% fill on white and on a scanned grey page, and
 *  ordered so the common 1-2 evidence case uses the two clearest. Fixed class
 *  strings, not interpolated: Tailwind purges anything it cannot see. */
const PALETTE = [
  { idle: 'bg-yellow-200/45 border-yellow-400/60 hover:bg-yellow-300/50',
    active: 'bg-yellow-300/50 border-yellow-500 ring-2 ring-yellow-300' },
  { idle: 'bg-sky-200/45 border-sky-400/60 hover:bg-sky-300/50',
    active: 'bg-sky-300/50 border-sky-500 ring-2 ring-sky-300' },
  { idle: 'bg-emerald-200/45 border-emerald-400/60 hover:bg-emerald-300/50',
    active: 'bg-emerald-300/50 border-emerald-500 ring-2 ring-emerald-300' },
  { idle: 'bg-fuchsia-200/45 border-fuchsia-400/60 hover:bg-fuchsia-300/50',
    active: 'bg-fuchsia-300/50 border-fuchsia-500 ring-2 ring-fuchsia-300' },
  { idle: 'bg-orange-200/45 border-orange-400/60 hover:bg-orange-300/50',
    active: 'bg-orange-300/50 border-orange-500 ring-2 ring-orange-300' },
] as const;

const ChunkHighlight: React.FC<ChunkHighlightProps> = ({
  bbox,
  pageWidth,
  pageHeight,
  isActive,
  subtle = false,
  onClick,
  chunkId,
  pointerThrough = false,
  fullWidth = false,
  colorIndex = 0,
}) => {
  // SBA chunk bboxes are persisted in PDF points.  Convert to
  // normalized percentages using the page dimensions before
  // rendering.  If the bbox already looks normalized (0-1
  // values), keep it as-is so the component remains tolerant of
  // future API normalization.
  const looksNormalized =
    bbox.x0 >= 0 && bbox.y0 >= 0 &&
    bbox.x1 <= 1.0001 && bbox.y1 <= 1.0001;

  const canNormalizeFromPage =
    typeof pageWidth === 'number' && pageWidth > 0 &&
    typeof pageHeight === 'number' && pageHeight > 0;

  const y0 = looksNormalized || !canNormalizeFromPage ? bbox.y0 : bbox.y0 / pageHeight;
  const y1 = looksNormalized || !canNormalizeFromPage ? bbox.y1 : bbox.y1 / pageHeight;

  // Honour the bbox's HORIZONTAL extent. `left:0% width:100%` used to be
  // unconditional, which painted an entire line for a six-word citation. The
  // sentence path now hands over rects already trimmed to the cited characters,
  // so the x values finally mean something.
  const x0 = looksNormalized || !canNormalizeFromPage ? bbox.x0 : bbox.x0 / pageWidth;
  const x1 = looksNormalized || !canNormalizeFromPage ? bbox.x1 : bbox.x1 / pageWidth;
  const left = fullWidth ? '0%' : `${x0 * 100}%`;
  const width = fullWidth ? '100%' : `${Math.max(0, x1 - x0) * 100}%`;
  const top = `${y0 * 100}%`;
  const height = `${(y1 - y0) * 100}%`;

  const palette = PALETTE[colorIndex % PALETTE.length]!;

  return (
    <div
      className={`absolute transition-colors duration-150 ${
        pointerThrough ? '' : 'cursor-pointer'
      } ${
        subtle
          ? 'border border-yellow-300/40 bg-transparent hover:bg-yellow-100/20'
          : isActive
            ? `border-2 ${palette.active} animate-pulse-once`
            : `border-2 ${palette.idle}`
      }`}
      style={{
        left, top, width, height, zIndex: 20,
        pointerEvents: pointerThrough ? 'none' : undefined,
        // A highlighter stroke, not a drawn box. Only meaningful now that the
        // rect hugs the words rather than spanning the page.
        borderRadius: fullWidth ? undefined : '0.15em',
      }}
      data-color-index={colorIndex}
      onClick={pointerThrough ? undefined : onClick}
      {...(pointerThrough ? {} : {
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') onClick?.();
        },
      })}
      aria-label={chunkId == null ? 'Cited evidence' : `Evidence chunk ${chunkId}`}
      {...(chunkId == null
        ? { 'data-page-anchor': 'true' }
        : { 'data-chunk-id': chunkId })}
    />
  );
};

export default ChunkHighlight;
